import { from_number_to_code } from './custom_predecessor';
import {
  createCustomLinkFormatterCommon,
  createDurationFormatter,
  renderLinks
} from './linkFormatterCommon';

/**
 * Cache for predecessor renderings.
 *
 * @type {WeakMap<Object, Object>}
 */
const predecessorsCache = new WeakMap();

/**
 * Internal cache for formatters per gantt instance.
 *
 * @type {WeakMap<Object, Object>}
 */
const predecessorFormattersCache = new WeakMap();

/**
 * Fallback renderer for the Predecessors column.
 *
 * @param {Object} activity - DHTMLX Gantt task object.
 * @returns {string} The raw predecessor string or empty if none.
 */
export function fallbackRenderer(activity) {
  return activity.custom_predecessors || '';
}

/**
 * Create a custom LinkFormatter wrapping the DHTMLX PRO linkFormatter.
 *
 * Uses correlative IDs and normalizes lag units to 'd'.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @param {Object} durationFormatter - The DurationFormatter to use for lag formatting.
 * @returns {Object} An object with `format(link): string` and `parse(text): Object` methods.
 * @see {@link https://docs.dhtmlx.com/gantt/desktop__formatters_ext.html#linkformatter}
 */
export function createCustomLinkFormatter(gantt, durationFormatter) {
  return createCustomLinkFormatterCommon(
    gantt,
    durationFormatter,
    'source',
    from_number_to_code
  );
}

/**
 * Render the predecessors for a Gantt task.
 *
 * Formats each link in `activity.$target` using the given formatter and caches the result.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @param {Object} formatter - LinkFormatter with a `format(link)` method.
 * @param {Object} activity - Gantt task object.
 * @returns {string} Semicolon-separated predecessor strings.
 */
export function renderPredecessors(gantt, formatter, activity) {
  return renderLinks(gantt, formatter, activity, '$target', predecessorsCache);
}

// Get or instantiate durationFormatter+linkFormatter for a gantt instance
function getPredecessorFormatters(gantt) {
  let entry = predecessorFormattersCache.get(gantt);
  if (!entry) {
    const durationFormatter = createDurationFormatter(gantt);
    // createCustomLinkFormatterCommon may return undefined in tests, default to empty object
    const rawLinkFormatter = createCustomLinkFormatter(
      gantt,
      durationFormatter
    );
    const linkFormatter = rawLinkFormatter || {};
    entry = { durationFormatter, linkFormatter };
    predecessorFormattersCache.set(gantt, entry);
  }
  return entry;
}

/**
 * Format predecessors for inline editor using the shared formatter.
 *
 * @param {Object} gantt - The DHTMLX Gantt instance.
 * @param {Object} activity - Gantt task object.
 * @returns {string} Formatted predecessor string for editing.
 */
export function formatTaskPredecessors(gantt, activity) {
  const { linkFormatter } = getPredecessorFormatters(gantt);
  return renderPredecessors(gantt, linkFormatter, activity);
}

/**
 * Invalidate the cached predecessors for a task.
 *
 * Should be called after link additions or deletions to refresh the cell.
 *
 * @param {Object} activity - Gantt task object to invalidate.
 */
export function invalidatePredecessorsCache(activity) {
  predecessorsCache.delete(activity);
}
